import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const CreativeIntelligenceDashboard = () => {
  const [patterns, setPatterns] = useState([]);
  const [fatigue, setFatigue] = useState([]);
  const [hypotheses, setHypotheses] = useState([]);
  const [intelligence, setIntelligence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('patterns');
  const [filters, setFilters] = useState({ vertical: '', channel: '', status: '' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.vertical) params.append('vertical', filters.vertical);
      if (filters.channel) params.append('channel', filters.channel);
      const qStr = params.toString() ? `?${params.toString()}` : '';

      const [patternsRes, fatigueRes, hypothesesRes, intelRes] = await Promise.all([
        axios.get(`${API}/api/creative-intelligence/top-patterns${qStr}`),
        axios.get(`${API}/api/creative-intelligence/fatigue-benchmarks${qStr}`),
        axios.get(`${API}/api/creative-intelligence/hypotheses?limit=30${filters.status ? `&status=${filters.status}` : ''}`),
        axios.get(`${API}/api/creative-intelligence${qStr}`),
      ]);

      const toArr = (d) => Array.isArray(d) ? d : d.records || d.patterns || d.benchmarks || d.hypotheses || [];
      setPatterns(toArr(patternsRes.data));
      setFatigue(toArr(fatigueRes.data));
      setHypotheses(toArr(hypothesesRes.data));
      setIntelligence(toArr(intelRes.data));
    } catch (err) {
      console.error('Failed to fetch creative intelligence:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateHypothesisStatus = async (id, status) => {
    try {
      await axios.post(`${API}/api/creative-intelligence/hypotheses/${id}/status?status=${status}`);
      fetchData();
    } catch (err) {
      console.error('Failed to update hypothesis:', err);
    }
  };

  const isEmpty = patterns.length === 0 && fatigue.length === 0 && hypotheses.length === 0 && intelligence.length === 0;

  const tabs = [
    { key: 'patterns', label: 'Top Patterns', count: patterns.length },
    { key: 'fatigue', label: 'Fatigue Benchmarks', count: fatigue.length },
    { key: 'hypotheses', label: 'Hypotheses', count: hypotheses.length },
    { key: 'intelligence', label: 'All Records', count: intelligence.length },
  ];

  return (
    <div className="space-y-6" data-testid="creative-intelligence-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white" data-testid="ci-title">Creative Intelligence</h1>
          <p className="text-slate-400 text-sm mt-1">Pattern analysis, fatigue benchmarks, and cross-category hypotheses</p>
        </div>
      </div>

      <div className="flex gap-3 flex-wrap" data-testid="ci-filters">
        <select
          value={filters.vertical}
          onChange={(e) => setFilters(f => ({ ...f, vertical: e.target.value }))}
          className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
          data-testid="filter-vertical"
        >
          <option value="">All Verticals</option>
          <option value="Health & Supplements">Health & Supplements</option>
          <option value="Beauty & Personal Care">Beauty</option>
          <option value="Technology">Technology</option>
          <option value="Fitness & Outdoors">Fitness</option>
          <option value="Gadgets & Novelty">Gadgets</option>
        </select>
        <select
          value={filters.channel}
          onChange={(e) => setFilters(f => ({ ...f, channel: e.target.value }))}
          className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
          data-testid="filter-channel"
        >
          <option value="">All Channels</option>
          <option value="META">Meta</option>
          <option value="TIKTOK">TikTok</option>
          <option value="YOUTUBE">YouTube</option>
          <option value="NATIVE">Native</option>
        </select>
        {activeTab === 'hypotheses' && (
          <select
            value={filters.status}
            onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
            className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
            data-testid="filter-status"
          >
            <option value="">All Statuses</option>
            <option value="generated">Generated</option>
            <option value="surfaced">Surfaced</option>
            <option value="tested">Tested</option>
            <option value="validated">Validated</option>
            <option value="invalidated">Invalidated</option>
          </select>
        )}
      </div>

      <div className="flex gap-1 border-b border-slate-800 pb-0" data-testid="ci-tabs">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            data-testid={`tab-${tab.key}`}
            className={`px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors border-b-2 -mb-px ${
              activeTab === tab.key
                ? 'text-emerald-400 border-emerald-400 bg-emerald-500/5'
                : 'text-slate-400 border-transparent hover:text-white hover:border-slate-600'
            }`}
          >
            {tab.label}
            <span className={`ml-2 px-1.5 py-0.5 text-xs rounded ${
              activeTab === tab.key ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-800 text-slate-500'
            }`}>{tab.count}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">Loading intelligence data...</div>
      ) : isEmpty ? (
        <div className="text-center py-16 space-y-4">
          <p className="text-slate-400 text-lg">No creative intelligence data yet</p>
          <p className="text-slate-500 text-sm">No creative intelligence yet. Patterns and hypotheses accumulate automatically as active campaigns generate performance data.</p>
        </div>
      ) : (
        <>
          {activeTab === 'patterns' && <PatternsTab data={patterns} />}
          {activeTab === 'fatigue' && <FatigueTab data={fatigue} />}
          {activeTab === 'hypotheses' && <HypothesesTab data={hypotheses} onStatusUpdate={updateHypothesisStatus} />}
          {activeTab === 'intelligence' && <IntelligenceTab data={intelligence} />}
        </>
      )}
    </div>
  );
};

const ConfidenceBadge = ({ tier }) => (
  <span className={`px-2 py-0.5 text-xs rounded font-medium ${
    tier === 'HIGH' ? 'bg-emerald-500/15 text-emerald-400' :
    tier === 'MEDIUM' ? 'bg-blue-500/15 text-blue-400' :
    'bg-slate-500/15 text-slate-400'
  }`}>{tier || 'LOW'}</span>
);

const PatternsTab = ({ data }) => (
  <div className="space-y-3" data-testid="patterns-section">
    {data.length === 0 ? (
      <p className="text-slate-500 text-sm py-8 text-center">No patterns found</p>
    ) : data.map((p, i) => (
      <div key={p.intelligence_id || i} className="bg-slate-900/50 border border-slate-800 rounded-xl p-5 card-hover" data-testid={`pattern-card-${i}`}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="text-white font-semibold">{p.attribute_value || p.vehicle || 'Unknown Pattern'}</span>
              <ConfidenceBadge tier={p.confidence_tier} />
              <span className="px-2 py-0.5 text-xs bg-purple-500/10 text-purple-400 rounded">{p.vertical}</span>
              <span className="px-2 py-0.5 text-xs bg-slate-800 text-slate-400 rounded">{p.channel}</span>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {p.vehicle && <span className="px-2 py-0.5 text-xs bg-slate-800/60 text-slate-300 rounded">{p.vehicle}</span>}
              {p.message && <span className="px-2 py-0.5 text-xs bg-slate-800/60 text-slate-300 rounded">{p.message}</span>}
              {p.visual_interrupt && <span className="px-2 py-0.5 text-xs bg-slate-800/60 text-slate-300 rounded">{p.visual_interrupt}</span>}
            </div>
          </div>
          <div className="text-right shrink-0 space-y-1">
            {p.avg_cpa != null && (
              <div>
                <span className="text-emerald-400 font-bold text-lg font-mono">${Number(p.avg_cpa).toFixed(2)}</span>
                <p className="text-slate-500 text-xs">Avg CPA</p>
              </div>
            )}
            <div className="flex gap-3 text-xs">
              {p.avg_ctr != null && <span className="text-blue-400 font-mono">{Number(p.avg_ctr).toFixed(2)}% CTR</span>}
              {p.win_rate != null && <span className="text-purple-400 font-mono">{(Number(p.win_rate) * 100).toFixed(0)}% WR</span>}
            </div>
            {p.sample_size != null && <p className="text-slate-600 text-xs">n={p.sample_size}</p>}
          </div>
        </div>
      </div>
    ))}
  </div>
);

const FatigueTab = ({ data }) => (
  <div className="space-y-3" data-testid="fatigue-section">
    {data.length === 0 ? (
      <p className="text-slate-500 text-sm py-8 text-center">No fatigue benchmarks found</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="fatigue-table">
          <thead>
            <tr>
              <th className="text-left p-3">Pattern</th>
              <th className="text-left p-3">Vertical</th>
              <th className="text-left p-3">Channel</th>
              <th className="text-left p-3">Vehicle</th>
              <th className="text-right p-3">Fatigue (days)</th>
              <th className="text-right p-3">Hook Runway</th>
              <th className="text-left p-3">Confidence</th>
            </tr>
          </thead>
          <tbody>
            {data.map((f, i) => (
              <tr key={f.intelligence_id || i} data-testid={`fatigue-row-${i}`}>
                <td className="p-3 text-white font-medium">{f.attribute_value || f.message || '-'}</td>
                <td className="p-3"><span className="px-2 py-0.5 text-xs bg-purple-500/10 text-purple-400 rounded">{f.vertical || '-'}</span></td>
                <td className="p-3 text-slate-400">{f.channel || '-'}</td>
                <td className="p-3 text-slate-400">{f.vehicle || '-'}</td>
                <td className="p-3 text-right font-mono text-emerald-400">{f.avg_fatigue_days ?? '-'}d</td>
                <td className="p-3 text-right font-mono text-blue-400">{f.avg_hook_injection_runway ?? '-'}d</td>
                <td className="p-3"><ConfidenceBadge tier={f.confidence_tier} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const HypothesesTab = ({ data, onStatusUpdate }) => (
  <div className="space-y-3" data-testid="hypotheses-section">
    {data.length === 0 ? (
      <p className="text-slate-500 text-sm py-8 text-center">No hypotheses found</p>
    ) : data.map((h, i) => {
      const status = h.hypothesis_status || 'generated';
      return (
        <div key={h.intelligence_id || i} className="bg-slate-900/50 border border-slate-800 rounded-xl p-5" data-testid={`hypothesis-card-${i}`}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className={`px-2 py-0.5 text-xs rounded font-medium ${
                  status === 'validated' ? 'bg-emerald-500/15 text-emerald-400' :
                  status === 'tested' ? 'bg-blue-500/15 text-blue-400' :
                  status === 'surfaced' ? 'bg-cyan-500/15 text-cyan-400' :
                  status === 'invalidated' ? 'bg-red-500/15 text-red-400' :
                  'bg-slate-500/15 text-slate-400'
                }`}>{status}</span>
                {h.hypothesis_source_vertical && (
                  <span className="text-slate-500 text-xs">{h.hypothesis_source_vertical} {h.hypothesis_source_channel ? `(${h.hypothesis_source_channel})` : ''} &rarr; {h.vertical || '?'}</span>
                )}
                {h.hypothesis_transfer_confidence != null && (
                  <span className="text-purple-400 text-xs font-mono">{Math.round(h.hypothesis_transfer_confidence * 100)}% transfer confidence</span>
                )}
              </div>
              <p className="text-white text-sm font-medium mb-1">{h.hypothesis_statement || h.attribute_value || 'No statement'}</p>
              {h.hypothesis_transfer_explanation && <p className="text-slate-400 text-xs mt-1">{h.hypothesis_transfer_explanation}</p>}
              {h.hypothesis_notes && <p className="text-slate-500 text-xs mt-1 italic">{h.hypothesis_notes}</p>}
            </div>
            <div className="flex gap-1.5 shrink-0">
              {status !== 'validated' && (
                <button
                  onClick={() => onStatusUpdate(h.intelligence_id, 'validated')}
                  data-testid={`validate-btn-${i}`}
                  className="px-3 py-1 text-xs bg-emerald-500/10 text-emerald-400 rounded hover:bg-emerald-500/20"
                >Validate</button>
              )}
              {status !== 'tested' && status !== 'validated' && (
                <button
                  onClick={() => onStatusUpdate(h.intelligence_id, 'tested')}
                  data-testid={`test-btn-${i}`}
                  className="px-3 py-1 text-xs bg-blue-500/10 text-blue-400 rounded hover:bg-blue-500/20"
                >Test</button>
              )}
              {status !== 'invalidated' && (
                <button
                  onClick={() => onStatusUpdate(h.intelligence_id, 'invalidated')}
                  data-testid={`invalidate-btn-${i}`}
                  className="px-3 py-1 text-xs bg-red-500/10 text-red-400 rounded hover:bg-red-500/20"
                >Reject</button>
              )}
            </div>
          </div>
        </div>
      );
    })}
  </div>
);

const IntelligenceTab = ({ data }) => (
  <div className="space-y-3" data-testid="intelligence-section">
    {data.length === 0 ? (
      <p className="text-slate-500 text-sm py-8 text-center">No intelligence records</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="intelligence-table">
          <thead>
            <tr>
              <th className="text-left p-3">Pattern</th>
              <th className="text-left p-3">Vertical</th>
              <th className="text-left p-3">Channel</th>
              <th className="text-left p-3">Confidence</th>
              <th className="text-right p-3">Win Rate</th>
              <th className="text-right p-3">CPA</th>
              <th className="text-right p-3">CTR</th>
              <th className="text-right p-3">Samples</th>
            </tr>
          </thead>
          <tbody>
            {data.map((rec, i) => (
              <tr key={rec.intelligence_id || i} data-testid={`intel-row-${i}`}>
                <td className="p-3 text-white font-medium">{rec.attribute_value || rec.vehicle || '-'}</td>
                <td className="p-3"><span className="px-2 py-0.5 text-xs bg-purple-500/10 text-purple-400 rounded">{rec.vertical || '-'}</span></td>
                <td className="p-3 text-slate-400">{rec.channel || '-'}</td>
                <td className="p-3"><ConfidenceBadge tier={rec.confidence_tier} /></td>
                <td className="p-3 text-right font-mono text-purple-400">{rec.win_rate ? `${(rec.win_rate * 100).toFixed(0)}%` : '-'}</td>
                <td className="p-3 text-right font-mono text-emerald-400">{rec.avg_cpa ? `$${Number(rec.avg_cpa).toFixed(2)}` : '-'}</td>
                <td className="p-3 text-right font-mono text-blue-400">{rec.avg_ctr ? `${Number(rec.avg_ctr).toFixed(2)}%` : '-'}</td>
                <td className="p-3 text-right font-mono text-slate-400">{rec.sample_size || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

export default CreativeIntelligenceDashboard;
